import {
  createDailyWorkCapacityEntry,
  fetchDailyWorkCapacityByDate,
  updateDailyWorkCapacityEntry,
} from "components/timesheet/acknowledgement/api";
import {
  invalidateTimesheetQueries,
  timesheetKeys,
} from "components/timesheet/queries";

import { TIMESHEET_QUERY_KEYS } from "../query-keys";
import { queryClient } from "utils/react-query";
import { selectUser } from "store";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";

/**
 * Hook for managing daily work capacity entries acknowledgement
 */
export function useAcknowledgementMutation() {
  const user = useSelector(selectUser);

  return useMutation({
    mutationFn: async (date) => {
      if (!date || !user?.id) {
        throw new Error("Date and user ID are required");
      }

      // Check if there's an existing capacity entry for this date
      const existingCapacity = await fetchDailyWorkCapacityByDate({
        date,
        employee: user.id,
      });

      if (existingCapacity?.is_logged) {
        return {
          success: true,
          isAlreadyLogged: true,
        };
      }

      // Perform the API update
      if (existingCapacity) {
        await updateDailyWorkCapacityEntry({
          id: existingCapacity.id,
          item: { is_logged: true },
        });
      } else {
        await createDailyWorkCapacityEntry({
          item: {
            employee: user.id,
            date,
            is_logged: true,
          },
        });
      }

      return {
        success: true,
        isAlreadyLogged: false,
      };
    },
    onMutate: async (date) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: timesheetKeys.acklmnt(date),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        timesheetKeys.acklmnt(date)
      );

      // Optimistically update the cache
      const newCapacityEntry = {
        employee: user.id,
        date,
        is_logged: true,
        id: Date.now(), // Temporary ID for optimistic update
      };

      queryClient.setQueryData(timesheetKeys.acklmnt(date), (old) => {
        if (!old) return [newCapacityEntry];
        const exists = old.some((entry) => entry.date === date);
        return exists
          ? old.map((entry) =>
              entry.date === date ? { ...entry, is_logged: true } : entry
            )
          : [...old, newCapacityEntry];
      });

      // Return a context object with the snapshotted value
      return { previousData, date };
    },
    onError: (error, date, context) => {
      // Rollback the cache to its previous state on error
      if (context?.previousData) {
        queryClient.setQueryData(
          timesheetKeys.acklmnt(context.date),
          context.previousData
        );
      }

      console.error("Error syncing daily work capacity:", error);
      toast.error("Failed to update acknowledgement");
    },
    onSuccess: (data, date) => {
      if (!data.isAlreadyLogged) {
        toast.success("Acknowledged successfully");
      }

      // Invalidate and refetch to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: timesheetKeys.acklmnt(date),
        exact: true,
      });
    },
  });
}

// Usage example:
// const acknowledgementMutation = useAcknowledgementMutation();
//
// const handleAcknowledge = (date) => {
//   acknowledgementMutation.mutate(date);
// };
//
// // Access mutation state:
// const { isPending, isError, error } = acknowledgementMutation;
